import { useEffect, useRef, useState } from "react";
import type { PDFDocumentProxy, RenderTask } from "pdfjs-dist";
import { loadPdfjs } from "../../lib/pdfjs";
import { outlineButtonClass } from "../ui";
import { useViewTrail } from "./useViewTrail";
import type { ContentItem } from "../../types";

interface PdfSlidesProps {
  item: ContentItem;
  moduleID: string;
  src: string;
  title: string;
  /** The PDF could not be shown here; the caller falls back to the browser's viewer. */
  onUnavailable: (reason: string) => void;
}

// Shows a PDF one page at a time and records which page was on screen and for
// how long, which the browser's own PDF viewer cannot tell the page.
export function PdfSlides({ item, moduleID, src, title, onUnavailable }: PdfSlidesProps) {
  const [doc, setDoc] = useState<PDFDocumentProxy | null>(null);
  const [page, setPage] = useState(1);
  const canvas = useRef<HTMLCanvasElement | null>(null);
  const box = useRef<HTMLDivElement | null>(null);
  const pageCount = doc?.numPages ?? 0;
  const viewed = useViewTrail(item, moduleID, "slides_page_viewed", pageCount);
  // The latest callback, without re-running the load whenever the parent renders.
  const unavailable = useRef(onUnavailable);
  useEffect(() => {
    unavailable.current = onUnavailable;
  });

  useEffect(() => {
    let active = true;
    let loaded: PDFDocumentProxy | null = null;
    loadPdfjs()
      .then((pdfjs) => pdfjs.getDocument({ url: src }).promise)
      .then((result) => {
        loaded = result;
        if (!active) return void result.destroy();
        setDoc(result);
      })
      .catch((error: unknown) => {
        if (active) unavailable.current(error instanceof Error ? error.message : "load failed");
      });
    return () => {
      active = false;
      void loaded?.destroy();
    };
  }, [src]);

  useEffect(() => {
    if (!doc) return undefined;
    let active = true;
    let task: RenderTask | null = null;
    doc
      .getPage(page)
      .then((pdfPage) => {
        const target = canvas.current;
        const context = target?.getContext("2d");
        if (!active || !target || !context) return undefined;
        const natural = pdfPage.getViewport({ scale: 1 });
        const width = box.current?.clientWidth || natural.width;
        const ratio = window.devicePixelRatio || 1;
        const viewport = pdfPage.getViewport({ scale: (width / natural.width) * ratio });
        target.width = Math.floor(viewport.width);
        target.height = Math.floor(viewport.height);
        target.style.width = "100%";
        task = pdfPage.render({ canvasContext: context, viewport });
        return task.promise;
      })
      .then(() => {
        if (active) viewed(page - 1, "open");
      })
      .catch((error: unknown) => {
        // A page replaced before it finished drawing is not a failure.
        if (active && (error as { name?: string })?.name !== "RenderingCancelledException") {
          unavailable.current(error instanceof Error ? error.message : "render failed");
        }
      });
    return () => {
      active = false;
      task?.cancel();
    };
    // `viewed` only reads refs and the page count, so it is not a trigger.
  }, [doc, page]);

  function go(next: number, by: "next" | "previous") {
    setPage(next);
    viewed(next - 1, by);
  }

  return (
    <div className="grid gap-3">
      <div ref={box} className="overflow-hidden rounded-md border border-app-line bg-app-field">
        <canvas ref={canvas} role="img" aria-label={`${title}, page ${page} of ${pageCount}`} />
      </div>
      <div className="flex items-center justify-between gap-3" role="group" aria-label="Pages">
        <button
          type="button"
          className={outlineButtonClass}
          disabled={page <= 1}
          onClick={() => go(page - 1, "previous")}
        >
          Previous
        </button>
        <span className="text-sm text-app-copy">{doc ? `${page} / ${pageCount}` : "…"}</span>
        <button
          type="button"
          className={outlineButtonClass}
          disabled={!doc || page >= pageCount}
          onClick={() => go(page + 1, "next")}
        >
          Next
        </button>
      </div>
    </div>
  );
}
